import { SSO_DATA } from './constants';
import { getCurrentEnv, getOktaClient, getCustomUrls } from './oktaService';
import { OKTA_AUTHENT_TYPE, setAuthenticatedWith } from 'services/loginManager';
import { PUBLIC_PATH, ROUTE } from 'utils/constants';

/**
 * Redirect the user to sso login to obtain necessary Okta tokens.
 *
 * @param locale the two-letters language code of the current language, i.e. `en` or `fr`
 * @param sessionToken optional Okta session token to skip the login form
 */
export const redirectToUnifiedLogin = (locale: string, sessionToken?: string): void => {
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCKS !== 'false') {
    simulateLocalSsoLogin();
    return;
  }

  const environment = getCurrentEnv();
  const language = locale.toUpperCase();

  const authClient = getOktaClient(locale);
  const customUrls = getCustomUrls(environment);
  authClient.token.getWithRedirect({
    responseType: ['token', 'id_token'],
    scopes: ['openid'],
    // state: locale,
    idp: SSO_DATA[environment].IDP[language],
    // custom urls for when the issuer url is not the base url of other requests
    ...customUrls,
    ...(sessionToken ? { sessionToken } : {}),
  });
};

/**
 * Simulate the SSO login flow for local development.
 * The real Okta IdP is configured to redirect SAML to localhost:3001 which doesn't exist.
 * This bypass stores a mock token and navigates directly as if the Okta flow succeeded.
 */
const simulateLocalSsoLogin = (): void => {
  const mockExpiresAt = Math.floor(Date.now() / 1000) + 3600;
  // Build a valid JWT structure so parseJwt() can decode it without crashing
  const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = btoa(JSON.stringify({ sub: 'LOCAL_DEV_USER', exp: mockExpiresAt, iss: 'local-dev' }));
  const mockJwt = `${header}.${payload}.mock-signature`;

  const mockAccessToken = {
    accessToken: mockJwt,
    expiresAt: mockExpiresAt,
    tokenType: 'Bearer',
    scopes: ['openid'],
  };

  // Store in the same cookie the customTokenStorage uses (key: Okta_accessToken)
  document.cookie = `Okta_accessToken=${encodeURIComponent(JSON.stringify(mockAccessToken))}; Path=/; SameSite=Lax`;

  setAuthenticatedWith(OKTA_AUTHENT_TYPE);

  // Navigate to welcome WITHOUT a hash — handleRedirect() will detect OKTA auth and proceed
  window.location.assign(`${PUBLIC_PATH}${ROUTE.WELCOME}`);
};
